import { WalletIcon } from "@phosphor-icons/react/dist/csr/Wallet";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { IcmPageBreakdownCard } from "@/icm/icm-page/icm-page-breakdown-card";
import { IcmPageSummaryCard } from "@/icm/icm-page/icm-page-summary-card";
import { AiScriptRunState } from "@/payroll/ai-script/ai-script-run-state";
import { LegacyScriptOutputState } from "@/payroll/pay-breakdown/legacy-script-output-state";
import { PAY_BREAKDOWN_RUN_TARGET_TYPE } from "@/payroll/pay-breakdown/types";
import { PayPeriodCard } from "@/payroll/pay-periods/pay-period-card";
import { LOCKED_RERUN_REASON } from "@/payroll/pay-periods/pay-period-lock";
import type { PayBreakdownPageState } from "./use-pay-breakdown-page";

interface PayBreakdownRunOutputProps {
  page: PayBreakdownPageState;
}

export const PayBreakdownRunOutput = ({ page }: PayBreakdownRunOutputProps) => {
  const {
    selectedPeriodStart,
    isLocked,
    output,
    selectedPeriodForCard,
    hasActiveRun,
    selectedPayrollRunStatus,
    visibleStatements,
    visibleJobs,
    isCreatingAiRun,
    icmRunDisabledReason,
    handleDownloadLegacyRun,
    handleRunPreferredPayroll,
    setSelectedJob,
    showFailedAiRun,
    showNoIcmRunYet,
    showNoLegacyRunYet,
    showIcmOutputLoading,
    showIcmNoResults,
    showIcmOutput,
  } = page;

  return (
    <Stack className="min-h-0 w-full flex-1" align="start" gap={4}>
      {showIcmOutputLoading && (
        <Spinner
          layout="centered"
          className="h-[400px]"
          size="lg"
          tone="primary"
        />
      )}
      {showIcmNoResults && (
        <Empty className={emptyStateVariants({ layout: "panel" })}>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <WalletIcon aria-hidden />
            </EmptyMedia>
            <EmptyTitle>No commissions were earned for this period.</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}
      {showIcmOutput && (
        <>
          <IcmPageSummaryCard
            statements={visibleStatements}
            jobs={visibleJobs}
          />
          <IcmPageBreakdownCard
            periodStart={selectedPeriodStart}
            statements={visibleStatements}
            jobs={visibleJobs}
            onSelectJob={setSelectedJob}
          />
        </>
      )}
      {output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.legacyScript && (
        <LegacyScriptOutputState
          run={output.run}
          onDownload={handleDownloadLegacyRun}
        />
      )}
      {showFailedAiRun && (
        <AiScriptRunState
          title="Payroll run failed"
          action="run"
          status={selectedPayrollRunStatus}
          isLoading={isCreatingAiRun || hasActiveRun}
          disabledReason={isLocked ? LOCKED_RERUN_REASON : undefined}
          onAction={handleRunPreferredPayroll}
        />
      )}
      {showNoIcmRunYet && selectedPeriodForCard && (
        <PayPeriodCard
          period={selectedPeriodForCard}
          mode="icm"
          disabledReason={icmRunDisabledReason}
        />
      )}
      {showNoLegacyRunYet && selectedPeriodForCard && (
        <PayPeriodCard period={selectedPeriodForCard} />
      )}
    </Stack>
  );
};
